import CloseIcon from '@mui/icons-material/Close';
import { Box, Snackbar } from '@mui/material';
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import * as React from 'react';
import { Form } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../../modules/translation/TranslationContext';
import ButtonWithFeddback from '../ButtonWithFeddback';

const useStyles = makeStyles()(() => ({
  formContent: {
    paddingBottom: 16,
    width: '100%',
  },
  buttonContent: {
    width: '100%',
  },
  buttonContainer: {
    width: 144,
  },
  buttonWrapper: {
    position: 'relative',
  },
  iconSmall: {
    fontSize: 20,
  },
}));

type CrudFormProps = {
  cancelLabel?: string;
  saveLabel?: string;
  children: React.ReactNode | ((props: any) => React.ReactNode);
  clearMessage: () => void;
  status?: 'edit' | 'success' | 'error';
  message: string;
  item: any;
  validate?: (values: any) => any | Promise<any>;
  getItem: () => void;
  onSubmit: (
    values: any,
    form: any,
    callback?: (errors?: any) => void,
  ) => any | Promise<any | undefined> | undefined | void;
  afterSubmit?: (values: any) => void;
  formProps?: any;
  disableSubmit?: boolean;
  onGoBack: (evt: React.MouseEvent<HTMLButtonElement>) => void;
  hideGoBack?: boolean;
  hideMessage?: boolean;
  classes?: { root?: string };
};

/**
 * @deprecated Use CrudFormAutoSave instead.
 */
function CrudForm({
  cancelLabel,
  saveLabel,
  children,
  clearMessage,
  status = 'edit',
  message,
  item,
  validate,
  onSubmit,
  afterSubmit,
  disableSubmit,
  onGoBack,
  hideGoBack,
  hideMessage,
  classes,
  formProps = {},
}: CrudFormProps) {
  const { classes: internalClasses } = useStyles();
  const { translate } = useTranslation();

  const getButtonStatus = (submitting: boolean) => {
    if (submitting) {
      return 'loading';
    }

    const buttonStateMap: any = {
      success: 'success',
      edit: 'normal',
      error: 'error',
    };

    return buttonStateMap[status];
  };

  return (
    <Box className={classes?.root}>
      <Form onSubmit={onSubmit} validate={validate} initialValues={item} {...formProps}>
        {({ handleSubmit, submitting, pristine, values, ...rest }) => (
          <form
            onSubmit={(event) => {
              const promise = handleSubmit(event);
              if (promise && afterSubmit) {
                promise.then(() => afterSubmit(rest.form));
              }
              return promise;
            }}
          >
            <Grid direction="column" container spacing={2}>
              <Grid size={12}>
                <div className={internalClasses.formContent}>
                  {typeof children === 'function' ? children({ values, ...rest }) : children}
                </div>
              </Grid>
              <Grid className={internalClasses.buttonContent} size={12}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    justifyContent: 'flex-end',
                  }}
                >
                  {!hideGoBack && (
                    <Grid className={internalClasses.buttonContainer}>
                      <Button
                        fullWidth
                        type="button"
                        onClick={onGoBack}
                        disabled={submitting}
                        disableElevation
                      >
                        {cancelLabel || translate(LangEnum.GLOBAL_GO_BACK)}
                      </Button>
                    </Grid>
                  )}

                  <Grid className={internalClasses.buttonContainer}>
                    <ButtonWithFeddback
                      fullWidth
                      disableElevation
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={submitting || disableSubmit}
                      status={getButtonStatus(submitting)}
                    >
                      {saveLabel || translate(LangEnum.GLOBAL_SAVE)}
                    </ButtonWithFeddback>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        )}
      </Form>
      {!hideMessage && (
        <Snackbar
          autoHideDuration={6000}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          open={!!message}
          action={[
            <IconButton onClick={clearMessage} size="large">
              <CloseIcon />
            </IconButton>,
          ]}
          message={<span id="message-id">{message}</span>}
          slotProps={{
            content: {
              'aria-describedby': 'message-id',
            },
          }}
        />
      )}
    </Box>
  );
}

export default CrudForm;
